import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { Switch as MaterialSwitch, Theme, Tooltip } from '@mui/material';
import { green, orange } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridRenderCellParams,
  GridRowData,
  GridSortModel,
  GridValueFormatterParams,
} from '@mui/x-data-grid';
import { AxiosResponse, CancelTokenSource } from 'axios';
import clsx from 'clsx';
import { OptionsObject, SnackbarKey } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation, UseMutationResult } from 'react-query';
import {
  generatePath,
  Link,
  NavLink,
  Route,
  Switch,
  useHistory,
} from 'react-router-dom';
import {
  SetterOrUpdater,
  useRecoilStateLoadable,
  useRecoilValueLoadable,
} from 'recoil';

import { useBusinessPermissions } from '../../../common/helperFunctions/useBusinessPermissions';
import {
  IUserAtom,
  // Role,
  user,
} from '../../../recoil/atoms/user';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import {
  IAlertWithProgressBar,
  useSnackbarWithStyles,
} from '../../components/notifications/SnackbarProviderWithStyles';
import dataTableScss from '../../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../../modules/Manage/pages/queues-table/EnhancedTableToolbar';
// import { IPermissions, permissionsSelector, } from '../../../recoil/selectors/permissionsSelector';
import { trackAddTeamSMS } from '../../../common/userTracking';
import { ActionColumn } from '../../modules/Manage/pages/queues-table/ActionColumns';

import { BusinessSMSDeleteDialog } from './BusinessSMSDeleteDialog';
import { BusinessSMSEditDialog } from './BusinessSMSEditDialog';
import { BusinessSMSMembersDialog } from './BusinessSMSMembersDialog';
import { BusinessSMSNumbersDialog } from './BusinessSMSNumbersDialog';

const BusinessSMSInboxToggle = (props: {
  params: GridRenderCellParams;
  mutation: UseMutationResult<AxiosResponse<any>, unknown, any, unknown>;
  enqueueSnackbarWithStyles: (
    alertComponentProps: IAlertWithProgressBar,
    snackbarProps?: OptionsObject | undefined
  ) => SnackbarKey;
  setRefetchToggleForBusinessSMS: SetterOrUpdater<any>;
  userAtom: IUserAtom;
  disabled?: boolean;
}) => {
  const [checked, setChecked] = useState<boolean>(
    Boolean(props.params.row.active === 1)
  );
  useEffect(() => {
    setChecked(Boolean(props.params.row.active === 1));
  }, [props.params.row.active]);
  const handleChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ): Promise<void> => {
    setChecked(checked);
    try {
      const response = await props.mutation.mutateAsync({
        endpoint: `businessSMS/updateStatus`,
        // stage: 'itn',
        domain: process.env.VITE_APP_CDK_DOMAIN,
        method: 'put',
        payload: {
          active: Number(checked ? 1 : 0),
          businessSMSId: Number(props.params.row.id),
          apikey: props.userAtom?.apikey,
          businessid: props.userAtom?.businessid,
          businessId: props.userAtom?.businessid,
        },
      });
      props.setRefetchToggleForBusinessSMS({});
      if (response?.data?.success === true) {
        props.enqueueSnackbarWithStyles({
          message: `Success updating team sms rule active status`,
          alertProps: { severity: 'success' },
        });
      }
    } catch (err) {
      props.enqueueSnackbarWithStyles({
        message: `Error updating team sms rule active status`,
        alertProps: { severity: 'error' },
      });
    }
  };
  return (
    <MaterialSwitch
      checked={checked}
      onChange={handleChange}
      color="primary"
      name="status"
      inputProps={{ 'aria-label': 'primary checkbox' }}
      disabled={props.disabled}
    />
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  fullWidth: {
    width: '100% !important',
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
  actionColumn: {
    display: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  numbersList: {
    padding: '0',
    paddingBottom: '1px',
    listStyleType: 'none',
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  actionButton: {
    marginLeft: 'auto',
    display: 'flex',
    flexWrap: 'nowrap',
  },
  peopleIconButton: {
    color: orange[500],
  },
}));

const BusinessSMSInbox = () => {
  const [selected, setSelected] = useState<GridRowData[]>([]);
  const [, setUserAtom] = useRecoilStateLoadable(user);
  const [, setDeleteCancelTokenSource] = useState<
    CancelTokenSource | undefined
  >();
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const history = useHistory();

  const userAtomLoadable = useRecoilValueLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  // const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  // const { role } = permissionsLoadable.contents as IPermissions;
  // const isSuperAdminBoolean = role === Role.SUPERADMIN;
  const [
    _,
    businessSMSLoadable,
    setRefetchToggleForBusinessSMS,
  ] = makeApiRequestCached(
    {
      method: 'get',
      // stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint:
        endpointEnum.BUSINESS_SMS_BY_BUSINESS +
        `/${userAtomContents?.businessid}` +
        '/',
      payloadJsonString: JSON.stringify({
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        businessId: userAtomContents?.businessid,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result ?? cached.current
  );
  useEffect(() => {
    setRefetchToggleForBusinessSMS({});
  }, []);

  const classes = useStyles();

  const dataUsers = {
    call: methodNameEnum.GET_BUSINESS_USERS,
  };

  const businessUsers = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify(dataUsers),
    })
  );

  const businessSMSInboxesCached = useRef([]);
  businessSMSInboxesCached.current = useMemo(
    () =>
      businessUsers.state === 'hasValue' &&
      businessSMSLoadable.state === 'hasValue'
        ? businessSMSLoadable.contents?.response?.data?.data
            ?.map((o) => ({
              ...o,
              id: o.businessSMSId,
            }))
            .sort((a, b) => a.id - b.id) ?? []
        : businessSMSInboxesCached.current,
    [businessSMSLoadable, businessUsers]
  );

  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...props}
        isLoading={businessSMSLoadable.state === 'loading'}
        dataType="BusinessSMS"
        numSelected={selected.length}
        rightComponent={
          <ToolbarAddDeleteButton
            dataPup={'add-delete-button'}
            numSelected={selected.length}
            onClickForAdd={() => {
              trackAddTeamSMS();
              history.push('/manage/numbers/business-sms/add');
            }}
            isLoading={props.isLoading}
            onClickForDelete={() => {
              history.push('/manage/numbers/business-sms/delete');
            }}
          />
        }
      />
    ),
    [selected.length, businessSMSLoadable]
  );
  const readOnly = useBusinessPermissions().accountReadOnly;
  const mutation = useMutation(
    getApiMutation(userAtomContents, setUserAtom, setDeleteCancelTokenSource)
  );

  const [deleteItemId, setDeleteItemId] = useState<
    string | number | undefined
  >();

  function nameFormatter(params: GridRenderCellParams) {
    return (
      <Tooltip title="Edit">
        <Link
          to={`/manage/numbers/business-sms/${params.row.businessSMSId}/edit`}
        >
          {params.value}
        </Link>
      </Tooltip>
    );
  }

  const handleDeletion = async (id: string | number) => {
    const response = await mutation.mutateAsync({
      endpoint: `${endpointEnum.BUSINESS_SMS_BY_BUSINESS}/${userAtomContents?.businessid}/${id}`,
      // stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      method: 'delete',
      payload: {
        businessSMSId: id,
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        businessId: userAtomContents?.businessid,
      },
    });
    setDeleteItemId(undefined);
    if (response?.data?.success !== true) {
      enqueueSnackbarWithStyles({
        message: `Encountered an error deleting the rule`,
        alertProps: { severity: 'error' },
      });
    } else {
      enqueueSnackbarWithStyles({
        message: `Successfully Deleted Team SMS Rule`,
        alertProps: { severity: 'success' },
      });
    }
    setRefetchToggleForBusinessSMS({});
  };

  const actions = ' ' + 'Actions' + '\xa0\xa0\xa0\xa0\xa0\xa0';
  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'businessSMSId',
        headerName: 'ID',
        width: 90,
      },
      {
        field: 'active',
        headerName: 'Status',
        width: 140,
        enableSorting: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          return (
            <>
              <div className={clsx(classes.actionColumn, 'row')}>
                <BusinessSMSInboxToggle
                  params={params}
                  mutation={mutation}
                  enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
                  setRefetchToggleForBusinessSMS={
                    setRefetchToggleForBusinessSMS
                  }
                  userAtom={userAtomContents}
                />
              </div>
            </>
          );
        },
        disableExport: true,
      },
      {
        field: 'name',
        headerName: 'Name',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        renderCell: nameFormatter,
      },
      {
        field: 'memberCount',
        headerName: 'Members',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'actions',
        headerName: actions,
        // headerClassName: dataTableScss.fullWidth,
        headerAlign: 'center',
        // cellClassName: dataTableScss.fullWidth,
        disableExport: true,
        width: 216,
        hide: readOnly,
        disableSearch: true, // do not search field content
        disableRowSelection: true,
        enableSorting: false,
        renderCell: ActionColumn({
          deleteItemId,
          setDeleteItemId,
          isLoading: mutation.isLoading,
          handleDeletion: handleDeletion,
          editLink: '/manage/numbers/business-sms/:id/edit',
          renderAction: (params: GridValueFormatterParams) => (
            <Tooltip title="Members">
              <NavLink
                to={generatePath(
                  '/manage/numbers/business-sms/:id/members',
                  params.row
                )}
              >
                <IconButton name="members" size="large">
                  <PeopleOutlineIcon className={classes.peopleIconButton} />
                </IconButton>
              </NavLink>
            </Tooltip>
          ),
          handleNumbersLink: '/manage/numbers/business-sms/:id/numbers',
        }),
      },
    ],
    [
      classes,
      deleteItemId,
      mutation.isLoading,
      userAtomContents?.user?.role,
      handleDeletion,
    ]
  );

  const [updatedRowId, setUpdatedRowId] = useState<string | undefined>();
  const [sortModel, setGridSortModel] = useState<GridSortModel>([
    { field: 'businessSMSId', sort: 'asc' },
  ]);

  return (
    <>
      <Switch>
        <Route path="/manage/numbers/business-sms/add">
          {({ history, match }) => (
            <BusinessSMSEditDialog
              show={match != null}
              onHide={() => {
                history.push('/manage/numbers/business-sms');
                setRefetchToggleForBusinessSMS({});
              }}
            />
          )}
        </Route>
        <Route path="/manage/numbers/business-sms/:id/numbers">
          {({ history, match }) => (
            <BusinessSMSNumbersDialog
              show={match != null}
              id={match?.params.id}
              setUpdatedRowId={setUpdatedRowId}
              onHide={() => {
                history.push('/manage/numbers/business-sms');
              }}
            />
          )}
        </Route>
        <Route path="/manage/numbers/business-sms/:id/members">
          {({ history, match }) => (
            <BusinessSMSMembersDialog
              show={match != null}
              id={match?.params.id}
              setUpdatedRowId={setUpdatedRowId}
              onHide={() => {
                history.push('/manage/numbers/business-sms');
              }}
            />
          )}
        </Route>
        <Route path="/manage/numbers/business-sms/:id/edit">
          {({ history, match }) => (
            <BusinessSMSEditDialog
              show={match != null}
              id={match?.params.id}
              setUpdatedRowId={setUpdatedRowId}
              onHide={() => {
                history.push('/manage/numbers/business-sms');
                setRefetchToggleForBusinessSMS({});
              }}
            />
          )}
        </Route>
        <Route path="/manage/numbers/business-sms/delete">
          {({ history, match }) => (
            <BusinessSMSDeleteDialog
              setRefetchToggleForBusinessSMS={setRefetchToggleForBusinessSMS}
              userAtomContents={userAtomContents}
              selected={selected}
              setSelected={setSelected}
              show={match != null}
              onHide={() => {
                history.push('/manage/numbers/business-sms');
              }}
            />
          )}
        </Route>
      </Switch>
      <DataTable
        enableSelectAll={true}
        rows={businessSMSInboxesCached.current ?? []}
        columns={columns}
        setSelected={setSelected}
        rowCount={
          businessSMSLoadable?.contents?.response?.data?.data?.length ?? 0
        }
        loading={
          businessSMSLoadable?.state === 'loading' ||
          businessUsers.state === 'loading'
        }
        ToolbarComponent={ToolbarComponent}
        sortModel={sortModel}
        setGridSortModel={setGridSortModel}
        updatedRowId={updatedRowId}
        selected={selected}
      />
    </>
  );
};

export default BusinessSMSInbox;
